/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// mantine
import { Box, Paper, Group, Image } from '@mantine/core';

// styles
import classes from './additionalities.module.css';

// components
import {
	ConfirmationModal,
	MenuFilter,
} from '@components/molecules';
import {
	AddFiltersButton,
	Button,
	Pagination,
} from '@components/atoms';

// i18n
import { useTranslation } from 'react-i18next';

// icons
import { IconCirclePlus } from '@tabler/icons-react';
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';

// configs
import { tableSchemaBase as columns } from '@features/resources/additionalities/additionalities.columns';

// hooks
import { useAdditionalities } from '@features/resources/additionalities/hooks/useAdditionalities';
import { Datatable } from '@components/organisms';

// types
import { TTargetColumn } from '@global-types/index';
import { useGetAdditionalities } from '@features/resources/additionalities/api/get-additionalities';
import {
	convertArrayToLabelValue,
	getVisibleColumns,
} from '@utils/functions';
import { PROJECT_TECHNOLOGIES } from '@config/constants/project-technologies';
import { useCountryCodes } from 'shared/api/get-country-codes';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import isNil from 'lodash/isNil';
import { useRemove } from '@features/resources/additionalities/api/remove';
import { DrawerAdditionalityProjectShowMore } from '@features/resources/additionalities/components/DrawerAdditionalityProjectShowMore';
import { useActivateAdditionalityProject } from '@features/resources/additionalities/api/activate-additionality-project';
import { AddAdditionalityProject } from '@features/resources/additionalities/components/AddAdditionalityProject';
import { UpdateAdditionalityProject } from '@features/resources/additionalities/components/UpdateAdditionalityProject';
import { TAdditionalityProjectPopulated } from '@features/resources/additionalities/types';
import { useOrganizationsHasAdditionalities } from '@api/get-organizations-has-additionalities';
import { useProducersHasAdditionalities } from '@api/get-producers-has-measurabilities';
import { ACTIVATED_FILTER_OPTIONS } from '@utils/options';
import { TAdditionalityProject } from '@global-types/api/api';
import {
	Authorization,
	useAuthorization,
} from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Additionalities = () => {
	/* ******************************** HOOKS ******************************* */
	const { t } = useTranslation();

	const {
		isOverflowFilters,
		ref,
		containerRef,
		showScrollFilters,
		handleScroll,
		handleAddItem,
		handleOrganizationFilter,
		handleViewMore,
		handleRowClicked,
		handleSortStatusChange,
		additionalitiesPage,
		additionalitiesSortStatus,
		additionalitiesPageSize,
		handlePaginationChangeSize,
		handleChangePagination,
		additionalitiesQueryParams,
		handleProducerFilter,
		handleTechnologyFilter,
		handleCountryFilter,
		additionalitiesSortParams,
		handleCloseConfirmationDeleteItem,
		additionalitiesOpenConfirmationModalDelete,
		handleConfirmDeleteItem,
		setAdditionalitiesOpenConfirmationModalDelete,
		openDrawer,
		setOpenDrawer,
		additionalitiesSelectedRow,
		setAddAdditionalityProjectFormOpened,
		addAdditionalityProjectFormOpened,
		setUpdateAdditionalityProjectFormOpened,
		updateAdditionalityProjectFormOpened,
		setAdditionalitiesDatatableOptions,
		additionalitiesDatatableOptions,
		setAdditionalitiesSelectedRow,
		setInitialAdditionalityData,
		handleActiveFilter,
	} = useAdditionalities();

	const { data, isFetching, total, totalPages } =
		useGetAdditionalities(
			additionalitiesPage,
			additionalitiesPageSize,
			additionalitiesQueryParams,
			additionalitiesSortParams
		);

	// APIs
	const { data: organizations } =
		useOrganizationsHasAdditionalities();

	const { data: producers } =
		useProducersHasAdditionalities();

	const { data: countryCodes } = useCountryCodes(
		'additionalities'
	);

	const remove = useRemove(
		additionalitiesPage,
		additionalitiesPageSize,
		additionalitiesQueryParams,
		additionalitiesSortParams
	);

	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? EUROPEAN_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	const { mutate } = useActivateAdditionalityProject(
		additionalitiesPage,
		additionalitiesPageSize,
		additionalitiesQueryParams,
		additionalitiesSortParams
	);

	const { checkAcl, checkAccess } = useAuthorization();

	return (
		<Box className={classes.container} ref={ref}>
			{/*Add project */}
			<AddAdditionalityProject
				onClose={() => {
					setAddAdditionalityProjectFormOpened(false);
				}}
				opened={addAdditionalityProjectFormOpened}
			/>
			{/*Update project */}
			<UpdateAdditionalityProject
				onClose={() => {
					setUpdateAdditionalityProjectFormOpened(false);
					setInitialAdditionalityData({});
				}}
				opened={updateAdditionalityProjectFormOpened}
				projectId={additionalitiesSelectedRow.id}
			/>
			{/*View more */}
			<DrawerAdditionalityProjectShowMore
				handleclose={() => {
					setOpenDrawer(false);
				}}
				opened={openDrawer}
				data={additionalitiesSelectedRow}
				position={'right'}
				handleactive={async (status: boolean) => {
					await mutate({
						active: status,
						ids: [additionalitiesSelectedRow.id],
					});
				}}
				handledelete={() => {
					setAdditionalitiesOpenConfirmationModalDelete({
						open: true,
						params: additionalitiesSelectedRow,
					});
				}}
				handleupdate={() => {
					setUpdateAdditionalityProjectFormOpened(true);
					setOpenDrawer(false);
				}}
			/>

			{/*Delete confirmation modal */}
			<ConfirmationModal
				title={t(
					'additionalities.alert.confirmationDelete.title'
				)}
				description={t(
					'additionalities.alert.confirmationDelete.description'
				)}
				onConfirm={() => {
					handleConfirmDeleteItem(remove);
					if (openDrawer) {
						setOpenDrawer(false);
					}
				}}
				onClose={handleCloseConfirmationDeleteItem}
				opened={
					additionalitiesOpenConfirmationModalDelete &&
					additionalitiesOpenConfirmationModalDelete.open
				}
			/>
			<Paper className={classes.paper}>
				{/* Row filters */}
				<Box className={classes.containerFilters}>
					<Box className={classes.wrapperRowLeft}>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('left');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToLeft
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
						<Box
							ref={containerRef}
							className={classes.wrapperFilters}
						>
							<Group gap={'0px'}>
								<Box
									className={classes.containerFilters5}
								>
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											onApply={handleActiveFilter}
											label={t('global.status')}
											size="lg"
											options={ACTIVATED_FILTER_OPTIONS}
											placeholderSearch={t(
												'filters.placeholderSearch.searchStatus'
											)}
											placeholder={t(
												'filters.placeholders.selectStatus'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Authorization
										allowedRoles={[
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										]}
									>
										<Box
											className={
												classes.containerFilterSelect
											}
										>
											<MenuFilter
												onApply={handleOrganizationFilter}
												label="organizations"
												size="lg"
												options={convertArrayToLabelValue(
													organizations
												)}
												placeholderSearch={t(
													'filters.placeholderSearch.searchOrganization'
												)}
												placeholder={t(
													'filters.placeholders.selectOrganization'
												)}
											/>
										</Box>
									</Authorization>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											onApply={handleProducerFilter}
											label={t('global.producer')}
											size="lg"
											options={convertArrayToLabelValue(
												producers
											)}
											placeholderSearch={t(
												'filters.placeholderSearch.searchProducer'
											)}
											placeholder={t(
												'filters.placeholders.selectProducer'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											value={
												additionalitiesQueryParams[
													'location.countryCode'
												]
													? additionalitiesQueryParams[
															'location.countryCode'
														]['$in']
													: []
											}
											isPersist
											onApply={handleCountryFilter}
											label={t('global.country')}
											size="lg"
											options={filteredCountries}
											placeholderSearch={t(
												'filters.placeholderSearch.searchCountry'
											)}
											placeholder={t(
												'filters.placeholders.selectCountry'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											isPersist
											value={
												additionalitiesQueryParams.technology
													? additionalitiesQueryParams.technology
													: []
											}
											onApply={handleTechnologyFilter}
											label="Project type"
											size="lg"
											options={PROJECT_TECHNOLOGIES}
											placeholderSearch={t(
												'filters.placeholderSearch.searchTechnology'
											)}
											placeholder={t(
												'filters.placeholders.selectTechnology'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Authorization
										allowedRoles={[
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										]}
									>
										<Box
											className={
												classes.containerFilterNumeric
											}
										>
											<AddFiltersButton>
												{t('global.addFilters')}
											</AddFiltersButton>
										</Box>
									</Authorization>
								</Box>
							</Group>
						</Box>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('right');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToRight
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
					</Box>
					{checkAcl(ACLS.ADDITIONALITIES.CREATE) &&
						checkAccess({
							allowedRoles: [
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							],
						}) && (
							<div className={classes.wrapperRowRight}>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									hiddenFrom="xxl"
									size="md"
									onClick={handleAddItem}
								>
									{t('global.add') +
										' ' +
										t('global.project')}
								</Button>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									visibleFrom="xxl"
									size="lg"
									onClick={handleAddItem}
								>
									{t('global.add') +
										' ' +
										t('global.project')}
								</Button>
							</div>
						)}
				</Box>
				<Box className={classes.spacerRow} />
				<Box className={classes.spacerRow} />
				{/* Datatable */}
				<Box className={classes.containerDatatable}>
					<Datatable
						onActive={async (
							row: TAdditionalityProjectPopulated
						) => {
							await mutate({
								active: !row.active,
								ids: [row.id],
							});
						}}
						handleUpdateColumns={(
							cols: TTargetColumn[]
						) => {
							setAdditionalitiesDatatableOptions(
								getVisibleColumns(columns, cols)
							);
						}}
						loading={
							isFetching
								? isFetching.toString()
								: undefined
						}
						allColumns={columns}
						columns={additionalitiesDatatableOptions}
						data={data}
						onRowClick={handleRowClicked}
						onEditItem={(row: TAdditionalityProject) => {
							setAdditionalitiesSelectedRow(row);
							setUpdateAdditionalityProjectFormOpened(
								true
							);
						}}
						onViewMore={handleViewMore}
						selectedRecords={null}
						onRemoveItem={(
							row: TAdditionalityProject
						) => {
							setAdditionalitiesOpenConfirmationModalDelete(
								{
									open: true,
									params: row,
								}
							);
						}}
						style={{
							minHeight: '100%',
						}}
						height={250}
						onSortStatusChange={handleSortStatusChange}
						sortstatus={additionalitiesSortStatus}
					/>
				</Box>
			</Paper>
			{/* Pagination */}
			<Box className={classes.containerPagination}>
				<Pagination
					pagesize={additionalitiesPageSize}
					totalitems={total}
					activepage={additionalitiesPage}
					handlechangesize={handlePaginationChangeSize}
					onChange={handleChangePagination}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};

export default Additionalities;
